import axios from 'axios';
import { withCache } from '../utils/requestCache';

// Configure base URL for API.
// Development (npm start) uses the local backend; production builds
// (npm run build / Vercel deploy) automatically use the live backend.
// Can be overridden with a REACT_APP_API_URL env variable.
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5000/api'
    : 'https://backend-deeps-projects-1a2428cf.vercel.app/api');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 20000,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
      if (!isAuthRequest) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Public API endpoints (no authentication required)
export const publicAPI = {
  getProducts: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return apiClient.get(`/products/public?${queryParams.toString()}`);
  },

  getProduct: (id) => {
    return apiClient.get(`/products/public/${id}`);
  },

  getFeaturedProducts: () => {
    return withCache('featured-products', () => apiClient.get('/products/public/featured'), 3 * 60 * 1000);
  },

  searchProducts: (query, filters = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    
    return apiClient.get(`/products/search?${queryParams.toString()}`);
  },

  getCollections: (options = {}) => {
    const light = options.light ? 'true' : 'false';
    const cacheKey = `collections-${light}`;
    const query = options.light ? '?light=true' : '';
    return withCache(cacheKey, () => apiClient.get(`/collections/public${query}`));
  },

  getCollection: (slug) => {
    return apiClient.get(`/collections/public/${slug}`);
  },

  getFeaturedCollections: () => {
    return apiClient.get('/collections/public/featured');
  },

  getTopCollections: () => {
    return apiClient.get('/collections/public/top');
  },

  debugCollections: () => {
    return apiClient.get('/collections/public/debug');
  },

  initSampleCollections: () => {
    return apiClient.post('/collections/public/init-sample');
  },

  // Get products by collection
  getProductsByCollection: (collectionId) => {
    return publicAPI.getProducts({ collection_id: collectionId });
  },

  // Landing page settings
  getLandingPageSettings: () => {
    return withCache('landing-page-settings', () => apiClient.get('/landing-page/public/settings'), 10 * 60 * 1000);
  },

  subscribeWhatsApp: (phone, source = 'homepage') => {
    return apiClient.post('/subscribers/public/subscribe', { phone, source });
  },

  validateCoupon: (code, subtotal, items = []) => {
    return apiClient.post('/coupons/validate', { code, subtotal, items });
  }
};

// Products API (with authentication for wishlist)
export const productsAPI = {
  // Wishlist management
  getWishlist: () => {
    return apiClient.get('/products/wishlist');
  },

  addToWishlist: (productId) => {
    return apiClient.post(`/products/wishlist/${productId}`);
  },

  removeFromWishlist: (productId) => {
    return apiClient.delete(`/products/wishlist/${productId}`);
  }
};

// Authenticated API endpoints
export const authAPI = {
  // Authentication
  login: (credentials) => {
    return apiClient.post('/auth/login', credentials);
  },

  register: (userData) => {
    return apiClient.post('/auth/register', userData);
  },

  getCurrentUser: () => {
    return apiClient.get('/auth/me');
  },

  logout: () => {
    return apiClient.post('/auth/logout');
  },

  // Profile management
  getProfile: () => {
    return apiClient.get('/auth/profile');
  },

  updateProfile: (profileData) => {
    return apiClient.put('/auth/profile', profileData);
  },

  addAddress: (addressData) => {
    return apiClient.post('/auth/profile/addresses', addressData);
  },

  updateAddress: (addressId, addressData) => {
    return apiClient.put(`/auth/profile/addresses/${addressId}`, addressData);
  },

  deleteAddress: (addressId) => {
    return apiClient.delete(`/auth/profile/addresses/${addressId}`);
  },

  // Orders (if implemented)
  createOrder: (orderData) => {
    return apiClient.post('/orders', orderData);
  },

  getOrders: () => {
    return apiClient.get('/orders');
  },

  getOrder: (orderId) => {
    return apiClient.get(`/orders/${orderId}`);
  },

  cancelOrder: (orderId) => {
    return apiClient.put(`/orders/${orderId}/cancel`);
  },

  trackOrder: (orderId) => {
    return apiClient.get(`/orders/${orderId}/tracking`);
  },

  // Razorpay payment
  createPaymentOrder: (paymentData) => {
    return apiClient.post('/payment/create-order', paymentData);
  }
};

// Admin API endpoints
export const adminAPI = {
  // Products
  getProducts: (params = {}) => {
    return apiClient.get('/products', { params });
  },

  getProduct: (id) => {
    return apiClient.get(`/products/${id}`);
  },

  createProduct: (productData) => {
    return apiClient.post('/products', productData);
  },

  updateProduct: (id, productData) => {
    return apiClient.put(`/products/${id}`, productData);
  },

  deleteProduct: (id) => {
    return apiClient.delete(`/products/${id}`);
  },

  updateProductStock: (id, sizeStock) => {
    return apiClient.put(`/products/${id}/stock`, sizeStock);
  },

  // Collections
  getCollections: () => {
    return apiClient.get('/collections');
  },

  getCollection: (id) => {
    return apiClient.get(`/collections/${id}`);
  },

  createCollection: (collectionData) => {
    return apiClient.post('/collections', collectionData);
  },

  updateCollection: (id, collectionData) => {
    return apiClient.put(`/collections/${id}`, collectionData);
  },

  deleteCollection: (id) => {
    return apiClient.delete(`/collections/${id}`);
  },

  // Orders
  getOrders: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    return apiClient.get(`/orders/admin?${queryParams.toString()}`);
  },

  getOrderDetail: (orderId) => {
    return apiClient.get(`/orders/admin/${orderId}`);
  },

  updateOrderStatus: (orderId, statusData) => {
    return apiClient.put(`/orders/admin/${orderId}/status`, statusData);
  },

  // Landing page management
  getLandingPageSettings: () => {
    return apiClient.get('/landing-page/settings');
  },

  uploadBackgroundImage: (formData) => {
    return apiClient.post('/landing-page/upload-background', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  updateLandingPageSettings: (settingsData) => {
    return apiClient.put('/landing-page/update-settings', settingsData);
  },

  resetLandingPageSettings: () => {
    return apiClient.post('/landing-page/reset-to-defaults');
  },

  // Stats
  getStats: () => {
    return apiClient.get('/products/admin/stats');
  }
};

// Utility functions
export const apiUtils = {
  // Handle API errors
  handleError: (error) => {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },

  // Format price
  formatPrice: (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  },

  isCloudinaryTransformSegment: (segment) => {
    if (!segment) return false;
    if (segment.includes(',')) return true;
    return /^(w_|h_|c_|g_|f_|q_|e_|dpr_|ar_|b_|a_|d_|l_|r_|t_|fl_)/.test(segment);
  },

  stripCloudinaryTransforms: (pathAfterUpload) => {
    const segments = pathAfterUpload.split('/');
    let start = 0;

    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i];
      if (/^v\d+$/.test(segment)) {
        start = i;
        break;
      }
      if (apiUtils.isCloudinaryTransformSegment(segment)) {
        start = i + 1;
        continue;
      }
      start = i;
      break;
    }

    return segments.slice(start).join('/');
  },

  applyCloudinaryTransforms: (url, transformString) => {
    const uploadMarker = '/upload/';
    const uploadIndex = url.indexOf(uploadMarker);
    if (uploadIndex === -1) return url;

    const prefix = url.slice(0, uploadIndex + uploadMarker.length);
    const suffix = url.slice(uploadIndex + uploadMarker.length);
    const assetPath = apiUtils.stripCloudinaryTransforms(suffix);

    return `${prefix}${transformString}/${assetPath}`;
  },

  // Optimize Cloudinary delivery URLs for faster loading
  getImageUrl: (imageUrl, options = {}) => {
    const normalizedOptions = typeof options === 'string'
      ? { fallback: options }
      : options;

    const {
      fallback = null,
      width = null,
      height = null,
      fit = 'limit'
    } = normalizedOptions;

    const url = imageUrl;
    if (!url || typeof url !== 'string') return fallback;

    if (!url.includes('res.cloudinary.com') || !url.includes('/upload/')) {
      return url;
    }

    const transforms = ['f_auto', 'q_auto:eco'];
    if (width) {
      if (fit === 'cover') {
        const targetHeight = height || Math.round(width * 1.25);
        transforms.push(`w_${width}`, `h_${targetHeight}`, 'c_fill', 'g_center');
      } else {
        transforms.push(`w_${width}`, 'c_limit');
      }
    }

    return apiUtils.applyCloudinaryTransforms(url, transforms.join(','));
  },

  getImagePlaceholder: (imageUrl, width = 32) => {
    if (!imageUrl || typeof imageUrl !== 'string') return null;
    if (!imageUrl.includes('res.cloudinary.com') || !imageUrl.includes('/upload/')) {
      return null;
    }
    return apiUtils.applyCloudinaryTransforms(
      imageUrl,
      `w_${width},e_blur:800,q_10,f_auto`
    );
  },

  // Check if product is available
  isProductAvailable: (product) => {
    return product.totalStock > 0;
  },

  // Check if size is available
  isSizeAvailable: (product, size) => {
    const sizeStock = product.sizeStock?.[size] || 0;
    return sizeStock > 0;
  },

  // Get available sizes for a product
  getAvailableSizes: (product) => {
    if (!product.sizes || !product.sizeStock) return [];
    
    return product.sizes.filter(size => {
      const stock = product.sizeStock[size] || 0;
      return stock > 0;
    });
  },

  // Get stock level for a size
  getSizeStock: (product, size) => {
    return product.sizeStock?.[size] || 0;
  },

  // Check if size has low stock
  isLowStock: (product, size, threshold = 5) => {
    const stock = apiUtils.getSizeStock(product, size);
    return stock > 0 && stock <= threshold;
  }
};

// Export default
const api = {
  publicAPI: publicAPI,
  products: productsAPI,
  auth: authAPI,
  admin: adminAPI,
  utils: apiUtils
};

export default api; 